/**
 * German wording and time formatting for the Verläufe live center.
 * Pure helpers; "now" is always passed in (server clock, see serverClock.ts).
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function parseTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Ticking duration: "1:42", "12:05", "1:02:09". Negative values count as 0. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
  return `${minutes}:${ss}`;
}

/** How long something has been waiting: "gerade eben", "seit 12 min", "seit 2 h", "seit 3 Tagen". */
export function formatSince(ms: number): string {
  if (ms < MINUTE_MS) return 'gerade eben';
  if (ms < HOUR_MS) return `seit ${Math.floor(ms / MINUTE_MS)} min`;
  if (ms < DAY_MS) return `seit ${Math.floor(ms / HOUR_MS)} h`;
  const days = Math.floor(ms / DAY_MS);
  return days === 1 ? 'seit 1 Tag' : `seit ${days} Tagen`;
}

/** What is left of the 24 h reply window: "noch 21 h", "noch 42 min"; null once it is over. */
export function formatWindowLeft(ms: number): string | null {
  if (ms <= 0) return null;
  if (ms >= HOUR_MS) return `noch ${Math.floor(ms / HOUR_MS)} h`;
  return `noch ${Math.max(1, Math.ceil(ms / MINUTE_MS))} min`;
}

function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function dayDiff(t: number, now: number): number {
  return Math.round((startOfDay(now) - startOfDay(t)) / DAY_MS);
}

function dateLabel(t: number, now: number): string {
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  const weekday = d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  const date = d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  // de-DE renders day+month as "28.09." (with the dot), with a year as "24.12.2025".
  return `${weekday}, ${date}`;
}

/** Day label: "Heute", "Gestern", "Morgen", "Mo, 29.09." (year only when it differs). */
export function formatDayLabel(iso: string | null | undefined, now: number): string | null {
  const t = parseTime(iso);
  if (t === null) return null;
  const diff = dayDiff(t, now);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Gestern';
  if (diff === -1) return 'Morgen';
  return dateLabel(t, now);
}

/** Local calendar day of a timestamp, for "did the day change" checks. */
export function dayKey(iso: string | null | undefined): string | null {
  const t = parseTime(iso);
  if (t === null) return null;
  const d = new Date(t);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Compact list time: "11:22" today, "Gestern", weekday within a week, else "29.09.". */
export function formatListTime(iso: string | null | undefined, now: number): string {
  const t = parseTime(iso);
  if (t === null) return '';
  const diff = dayDiff(t, now);
  const d = new Date(t);
  if (diff <= 0) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return 'Gestern';
  if (diff < 7) return d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    ...(sameYear ? {} : { year: '2-digit' }),
  });
}

export function formatTime(iso: string | null | undefined): string | null {
  const t = parseTime(iso);
  if (t === null) return null;
  return new Date(t).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

/** "Mo, 29.09. · 14:03" for the context column. */
export function formatDateTime(iso: string | null | undefined, now: number): string | null {
  const t = parseTime(iso);
  if (t === null) return null;
  return `${formatDayLabel(iso, now)} · ${formatTime(iso)}`;
}

/** Outcome of a session (conversation `status`, see conversation_summary_service). */
const OUTCOME_LABELS: Record<string, string> = {
  booking_complete: 'Termin gebucht',
  cancel_complete: 'Termin storniert',
  reschedule_complete: 'Termin verschoben',
  booking_failed: 'Buchung abgelehnt',
  transferred: 'Weitergeleitet',
  consent_refused: 'Keine Einwilligung',
  no_response: 'Keine Antwort mehr',
  max_duration: 'Maximale Dauer erreicht',
  no_answer: 'Nicht angenommen',
  busy: 'Besetzt',
  failed: 'Technisch fehlgeschlagen',
  canceled: 'Abgebrochen',
  hangup: 'Beendet',
};

/** Readable outcome, or null for an open/unknown session (nothing worth a label). */
export function formatOutcome(status: string | null | undefined): string | null {
  if (!status) return null;
  return OUTCOME_LABELS[status] ?? null;
}

export function formatPhone(phone: string | null | undefined): string {
  const trimmed = (phone ?? '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
}

/** Digits only, for comparing numbers written differently (+49…, 49…). */
export function phoneDigits(phone: string | null | undefined): string {
  return (phone ?? '').replace(/\D/g, '');
}

export function displayName(name: string | null | undefined, phone: string | null | undefined): string {
  return name?.trim() || formatPhone(phone) || 'Unbekannt';
}

/** Two letters for the avatar ("Anna Meier" → "AM"); the last two digits for a bare number. */
export function initials(name: string | null | undefined, phone: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const digits = phoneDigits(phone);
  return digits ? digits.slice(-2) : '#';
}

/**
 * `call.step` label ("sucht Termine …", progressive form, sent when a tool
 * starts) as a quiet line: "Agent sucht Termine …" while it runs, without the
 * ellipsis once a newer step or line superseded it.
 */
export function formatStep(label: string | null | undefined, opts: { running?: boolean } = {}): string | null {
  const text = (label ?? '').trim().replace(/\s*(?:…|\.{3})$/, '').trim();
  if (!text) return null;
  return opts.running === false ? `Agent ${text}` : `Agent ${text} …`;
}
